import Menu from "components/common/Menu";
import { SideBarTabs } from "constants/tabs";
import { formatDate, getPrice } from "functions/text";
import { useTabActive } from "hook/useTabActive";
import React, { useCallback, useEffect, useState } from "react";
import { IoCall, IoRefresh, IoSearch } from "react-icons/io5";
import { useLocation } from "react-router-dom";
import {
  CreateSparePartProduct,
  GetSparePartCategories,
  GetSparePartProducts,
  GetSparePartRequests,
  UpdateSparePartRequest,
} from "api/services/spareParts";
import {
  SparePartCategoryType,
  SparePartProductType,
  SparePartRequestStatusType,
  SparePartRequestType,
} from "types/loadedData";
import { CommonStyles } from "styles/CommonStyles";

const requestStatuses: SparePartRequestStatusType[] = [
  "New",
  "Contacted",
  "Sourcing",
  "Quoted",
  "Ordered",
  "Delivered",
  "Cancelled",
];

const emptyProduct = {
  title: "",
  categorySlug: "",
  categoryName: "",
  make: "",
  model: "",
  minYear: "",
  maxYear: "",
  partType: "Unknown",
  price: "",
  quoteOnly: true,
  stockStatus: "Order only",
  city: "",
  image: "",
};

function SparePartsPage() {
  const location = useLocation();
  const selectedRequestId = String(location.state?.requestId || "");
  const [activeTab, setActiveTab] = useState<"requests" | "products">("requests");
  const [categories, setCategories] = useState<SparePartCategoryType[]>([]);
  const [requests, setRequests] = useState<SparePartRequestType[]>([]);
  const [products, setProducts] = useState<SparePartProductType[]>([]);
  const [requestSearch, setRequestSearch] = useState("");
  const [requestStatus, setRequestStatus] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [savingProduct, setSavingProduct] = useState(false);
  const [productForm, setProductForm] = useState<any>(emptyProduct);

  useTabActive(SideBarTabs.SpareParts.name);

  const loadCategories = useCallback(async () => {
    const response = await GetSparePartCategories();
    if (response.status === 200) setCategories(response.categories);
  }, []);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const response = await GetSparePartRequests({
        search: requestSearch,
        status: requestStatus,
        limit: 80,
      });
      if (response.status === 200) setRequests(response.requests);
    } finally {
      setLoading(false);
    }
  }, [requestSearch, requestStatus]);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await GetSparePartProducts({
        search: productSearch,
        limit: 80,
      });
      if (response.status === 200) setProducts(response.products);
    } finally {
      setLoading(false);
    }
  }, [productSearch]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (activeTab === "requests") loadRequests();
      else loadProducts();
    }, 250);
    return () => clearTimeout(timeout);
  }, [activeTab, loadProducts, loadRequests]);

  useEffect(() => {
    if (!selectedRequestId) return;
    setActiveTab("requests");
    if (!requests.length) return;
    const frame = window.requestAnimationFrame(() => {
      document
        .getElementById(`spare-request-${selectedRequestId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [requests, selectedRequestId]);

  const handleRequestStatus = async (
    request: SparePartRequestType,
    status: SparePartRequestStatusType
  ) => {
    const response = await UpdateSparePartRequest({
      id: request._id,
      status,
      quotedPrice: request.quotedPrice || 0,
      staffNotes: request.staffNotes || "",
    });
    if (response.status === 200) {
      setRequests((current) =>
        current.map((item) => (item._id === request._id ? response.request : item))
      );
    }
  };

  const handleRequestField = async (
    request: SparePartRequestType,
    field: "quotedPrice" | "staffNotes",
    value: string
  ) => {
    const response = await UpdateSparePartRequest({
      id: request._id,
      status: request.status,
      quotedPrice: field === "quotedPrice" ? value : request.quotedPrice || 0,
      staffNotes: field === "staffNotes" ? value : request.staffNotes || "",
    });
    if (response.status === 200) {
      setRequests((current) =>
        current.map((item) => (item._id === request._id ? response.request : item))
      );
    }
  };

  const handleCategorySelect = (slug: string) => {
    const category = categories.find((item) => item.slug === slug);
    setProductForm((current: any) => ({
      ...current,
      categorySlug: slug,
      categoryName: category?.name || current.categoryName,
    }));
  };

  const handleCreateProduct = async () => {
    if (!productForm.title || !productForm.categorySlug) return;
    setSavingProduct(true);
    try {
      const response = await CreateSparePartProduct({
        ...productForm,
        minYear: productForm.minYear ? Number(productForm.minYear) : undefined,
        maxYear: productForm.maxYear ? Number(productForm.maxYear) : undefined,
        price: productForm.price ? Number(productForm.price) : 0,
      });
      if (response.status === 201) {
        setProducts((current) => [response.product, ...current]);
        setProductForm(emptyProduct);
      }
    } finally {
      setSavingProduct(false);
    }
  };

  const renderRequests = () => (
    <div className={ClassName.section}>
      <div className={ClassName.toolbar}>
        <div className={ClassName.searchBox}>
          <IoSearch size={18} />
          <input
            value={requestSearch}
            onChange={(event) => setRequestSearch(event.target.value)}
            placeholder="Search phone, part, make, model, city"
            className={ClassName.searchInput}
          />
        </div>
        <select
          value={requestStatus}
          onChange={(event) => setRequestStatus(event.target.value)}
          className={ClassName.select}
        >
          <option value="">All statuses</option>
          {requestStatuses.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
        <button className={ClassName.refreshButton} onClick={loadRequests}>
          <IoRefresh size={18} />
        </button>
      </div>
      <div className={ClassName.list}>
        {loading ? (
          <div className="primaryLoader mx-auto my-10"></div>
        ) : (
          requests.map((request) => (
            <div
              id={`spare-request-${request._id}`}
              key={request._id}
              className={`${ClassName.requestCard} ${
                selectedRequestId === String(request._id)
                  ? ClassName.selectedRequestCard
                  : ""
              }`}
            >
              <div className={ClassName.requestTop}>
                <div>
                  <span className={ClassName.requestTitle}>
                    {request.year} {request.make} {request.model}
                  </span>
                  <p className={ClassName.requestSub}>
                    {request.categoryName} {request.position ? `| ${request.position}` : ""}
                    {request.city ? ` | ${request.city}` : ""}
                  </p>
                </div>
                <select
                  value={request.status}
                  onChange={(event) =>
                    handleRequestStatus(
                      request,
                      event.target.value as SparePartRequestStatusType
                    )
                  }
                  className={ClassName.statusSelect}
                >
                  {requestStatuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
              <div className={ClassName.detailsGrid}>
                <div>
                  <span className={ClassName.label}>Customer</span>
                  <p>{request.user?.name || "-"}</p>
                </div>
                <div>
                  <span className={ClassName.label}>Phone</span>
                  <a href={`tel:${request.phone}`} className={ClassName.phone}>
                    <IoCall size={14} /> {request.phone}
                  </a>
                </div>
                <div>
                  <span className={ClassName.label}>Qty</span>
                  <p>{request.quantity || 1}</p>
                </div>
                <div>
                  <span className={ClassName.label}>Date</span>
                  <p>{formatDate(request.createdAt)}</p>
                </div>
              </div>
              {request.photo && (
                <a href={request.photo} target="_blank" rel="noreferrer">
                  <img src={request.photo} alt="Requested spare part" className={ClassName.photo} />
                </a>
              )}
              {request.notes && <p className={ClassName.notes}>{request.notes}</p>}
              <div className={ClassName.editRow}>
                <input
                  defaultValue={request.quotedPrice || ""}
                  placeholder="Quoted price"
                  className={ClassName.input}
                  onBlur={(event) =>
                    handleRequestField(request, "quotedPrice", event.target.value)
                  }
                />
                <input
                  defaultValue={request.staffNotes || ""}
                  placeholder="Staff notes"
                  className={ClassName.inputWide}
                  onBlur={(event) =>
                    handleRequestField(request, "staffNotes", event.target.value)
                  }
                />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderProducts = () => (
    <div className={ClassName.section}>
      <div className={ClassName.productForm}>
        <input
          value={productForm.title}
          onChange={(event) =>
            setProductForm((current: any) => ({ ...current, title: event.target.value }))
          }
          placeholder="Part title"
          className={ClassName.input}
        />
        <select
          value={productForm.categorySlug}
          onChange={(event) => handleCategorySelect(event.target.value)}
          className={ClassName.select}
        >
          <option value="">Category</option>
          {categories.map((category) => (
            <option key={category.slug} value={category.slug}>
              {category.name}
            </option>
          ))}
        </select>
        {["make", "model", "city", "image"].map((field) => (
          <input
            key={field}
            value={productForm[field]}
            onChange={(event) =>
              setProductForm((current: any) => ({
                ...current,
                [field]: event.target.value,
              }))
            }
            placeholder={field === "image" ? "Image URL" : field}
            className={ClassName.input}
          />
        ))}
        <input
          value={productForm.price}
          onChange={(event) =>
            setProductForm((current: any) => ({ ...current, price: event.target.value }))
          }
          placeholder="Price"
          className={ClassName.input}
        />
        <select
          value={String(productForm.quoteOnly)}
          onChange={(event) =>
            setProductForm((current: any) => ({
              ...current,
              quoteOnly: event.target.value === "true",
            }))
          }
          className={ClassName.select}
        >
          <option value="true">Quote only</option>
          <option value="false">Show price</option>
        </select>
        <button
          className={ClassName.primaryButton}
          onClick={handleCreateProduct}
          disabled={savingProduct}
        >
          {savingProduct ? "Saving..." : "Add product"}
        </button>
      </div>
      <div className={ClassName.toolbar}>
        <div className={ClassName.searchBox}>
          <IoSearch size={18} />
          <input
            value={productSearch}
            onChange={(event) => setProductSearch(event.target.value)}
            placeholder="Search catalog products"
            className={ClassName.searchInput}
          />
        </div>
      </div>
      <div className={ClassName.productGrid}>
        {products.map((product) => (
          <div key={product._id} className={ClassName.productCard}>
            {product.image && <img src={product.image} alt={product.title} className={ClassName.productImage} />}
            <span className={ClassName.requestTitle}>{product.title}</span>
            <p className={ClassName.requestSub}>
              {[product.categoryName, product.make, product.model, product.city]
                .filter(Boolean)
                .join(" | ")}
            </p>
            <p className={ClassName.price}>
              {product.quoteOnly || !product.price
                ? "Price on request"
                : getPrice(product.price)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className={CommonStyles.pageWrapper}>
      <Menu />
      <div className={ClassName.container}>
        <div className={ClassName.header}>
          <div>
            <h1 className={ClassName.title}>Spare Parts</h1>
            <p className={ClassName.subtitle}>
              Manage EV/body-part requests and simple BossCar catalog items.
            </p>
          </div>
          <div className={ClassName.tabs}>
            <button
              className={`${ClassName.tab} ${
                activeTab === "requests" ? ClassName.tabActive : ""
              }`}
              onClick={() => setActiveTab("requests")}
            >
              Requests
            </button>
            <button
              className={`${ClassName.tab} ${
                activeTab === "products" ? ClassName.tabActive : ""
              }`}
              onClick={() => setActiveTab("products")}
            >
              Catalog
            </button>
          </div>
        </div>
        {activeTab === "requests" ? renderRequests() : renderProducts()}
      </div>
    </div>
  );
}

const ClassName = {
  container: "flex flex-col flex-1 p-4 xl:p-8 gap-4 text-primaryText",
  header:
    "bg-white shadow-primaryShadow p-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4",
  title: "text-2xl font-semibold",
  subtitle: "text-secondaryText mt-1",
  tabs: "flex gap-2",
  tab: "px-4 py-2 rounded-md bg-bgSecondary",
  tabActive: "bg-primary text-white",
  section: "bg-white shadow-primaryShadow p-4 flex flex-col gap-4",
  toolbar: "flex flex-col md:flex-row gap-3",
  searchBox:
    "flex items-center gap-2 bg-bgSecondary rounded-md px-3 h-11 flex-1",
  searchInput: "bg-transparent outline-none flex-1",
  select: "bg-bgSecondary rounded-md px-3 h-11 outline-none",
  refreshButton:
    "h-11 w-11 rounded-md bg-bgSecondary flex items-center justify-center",
  list: "flex flex-col gap-3 max-h-[72vh] overflow-auto scroll-hide",
  requestCard: "border border-borderPrimary rounded-lg p-4 flex flex-col gap-3",
  selectedRequestCard: "border-primary bg-red-50 ring-2 ring-primary/30",
  requestTop: "flex items-start justify-between gap-3",
  requestTitle: "font-semibold text-base",
  requestSub: "text-sm text-secondaryText mt-1",
  statusSelect: "bg-bgSecondary rounded-md px-3 py-2 outline-none",
  detailsGrid: "grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm",
  label: "text-xs text-secondaryText",
  phone: "text-primary flex items-center gap-1",
  photo: "w-32 h-24 object-cover rounded-md",
  notes: "text-sm bg-bgSecondary rounded-md p-3",
  editRow: "flex flex-col md:flex-row gap-2",
  input: "bg-bgSecondary rounded-md px-3 h-11 outline-none",
  inputWide: "bg-bgSecondary rounded-md px-3 h-11 outline-none flex-1",
  productForm: "grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-2",
  primaryButton: "bg-primary text-white rounded-md px-4 h-11 disabled:opacity-60",
  productGrid: "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3",
  productCard: "border border-borderPrimary rounded-lg p-4",
  productImage: "w-full h-36 object-cover rounded-md mb-3",
  price: "text-primary font-semibold mt-2",
};

export default SparePartsPage;
